import { CSSProperties, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ChatDefaultView, ChatWidget, CustomerPriority } from '@hevoio/hevo-ui-toolkit';
import type { FeedbackReasonOption } from '@hevoio/hevo-ui-toolkit';
import { useObservableState } from '../../../../core/hooks/useObservableState';
import useService from '../../../../injector/hooks/useService';
import { getUnreadDisplayCount } from '../../../../core/utils/string';
import { TRACKER_SUPPORT_CTA_CLICK } from '../../../../core/constants/constants';
import { EnvConfig } from '../../../../core/env-config';
import { SupportService } from '../../../../support/interfaces/support';
import { TeamService } from '../../../../core/services/team.service';
import useTeamSettings from '../../../../containers/team/hooks/useTeamSettingsService';
import useAnalyticsTracker from '../../../../analytics/hooks/useAnalyticsTracker';
import { makeCerebraAIConfig, makeCerebraStorage, makeCerebraZendeskConfig } from '../../chat-widget/CerebraAPI';
import { ChatbotEventTracker, ChatbotTracking } from '../../chat-widget/chatbot-tracking';
import { AppbarSupportCTA } from './AppbarSupportCTA';
import { AppbarSupportCTAWithMenu } from './AppbarSupportCTAWithMenu';

declare global {
  interface Window {
    // Boot-time guard (set in index.html) that suppresses the Zendesk messenger auto
    // restore-open in a Cerebra deployment; cleared when the user intentionally opens Zendesk.
    __hevoSuppressZendeskChat?: boolean;
  }
}

// Predefined reasons shown in the dislike / raise-issue feedback dropdowns. Each option's
// `label` is displayed while its `value` is what the widget forwards to
// submitFeedback/raiseIssue (as `reason`); "Others" is appended by the widget and reveals a
// free-text comment box. Values are stable machine codes so the backend isn't tied to copy.
const FEEDBACK_REASONS: FeedbackReasonOption[] = [
  { label: 'Report Issue or Feature Request', value: 'reason_report_issue_or_feature_request' },
  {
    label: 'AI response is incorrect or incomplete',
    value: 'reason_ai_response_is_incorrect_or_incomplete'
  },
  { label: 'AI response is very generic', value: 'reason_ai_response_is_very_generic' },
  { label: 'Account-specific custom query', value: 'reason_account_specific_custom_query' }
];

// Draggable-FAB tuning: how far a pointer must move before it counts as a drag (vs a
// tap that expands the widget), and the min gap kept from the viewport edges.
const DRAG_THRESHOLD_PX = 4;
const VIEWPORT_MARGIN_PX = 8;

/**
 * Clamp a bottom-right-anchored box so it stays fully within the viewport (with a small
 * margin). We anchor by `right`/`bottom` — not `left`/`top` — because the widget's FAB and
 * its expanded panel share a bottom-right corner: keeping that corner fixed means the FAB
 * returns to exactly where it was dragged after the panel is opened and closed.
 */
function clampToViewport(right: number, bottom: number, width: number, height: number) {
  return {
    right: Math.min(Math.max(VIEWPORT_MARGIN_PX, right), window.innerWidth - width - VIEWPORT_MARGIN_PX),
    bottom: Math.min(Math.max(VIEWPORT_MARGIN_PX, bottom), window.innerHeight - height - VIEWPORT_MARGIN_PX)
  };
}

export function AppbarSupportBtnWrapper({ expanded = false }: { expanded?: boolean }) {
  const teamDetailsService = useService(TeamService);
  const supportService = useService(SupportService);

  const isTeamInTrial = teamDetailsService.teamDetails?.status === 'TRIAL';
  const [supportUnreadCount] = useObservableState(supportService.unreadCount$);
  const [canShowOnCallSupportIndicator] = useObservableState(supportService.isOnCallSupportAvailable$);
  const [supportPersonImageURL] = useObservableState(supportService.onCallSupportPersonImage$);
  // Show the chatbot only when the deployment build flag is on AND the per-user enabled
  // API (resolved once on login, published on chatbotEnabled$) says so.
  const { isCerebraEnabled } = useTeamSettings();
  const [isChatbotApiEnabled] = useObservableState(supportService.chatbotEnabled$);
  const isChatbotEnabled = isCerebraEnabled && isChatbotApiEnabled;

  // Cerebra shows its own unread badge on its FAB, so suppress the legacy Zendesk unread
  // counter on the appbar when the chatbot is enabled.
  const zendeskUnreadCount = isChatbotEnabled ? 0 : supportUnreadCount;

  // The widget is always mounted (when chatbot is enabled) as a FAB; `minimized`
  // is its controlled state. Both the appbar CTA and the widget's own FAB expand it.
  const [minimized, setMinimized] = useState(true);

  // useAnalyticsTracker returns a fresh object each render. Keep the latest in a
  // ref so `track` can stay referentially stable — the config factories are
  // memoized and the widget reads them from a ref, so a changing tracker identity
  // must not force them to be rebuilt.
  const analyticsTracker = useAnalyticsTracker();
  const analyticsTrackerRef = useRef(analyticsTracker);
  analyticsTrackerRef.current = analyticsTracker;
  const track = useCallback<ChatbotEventTracker>((event, properties) => {
    analyticsTrackerRef.current.eventTrack({ action: event, properties });
  }, []);

  const storage = useMemo(() => makeCerebraStorage(), []);
  const aiConfig = useMemo(() => makeCerebraAIConfig(track), [track]);
  const zendeskConfig = useMemo(() => makeCerebraZendeskConfig(track), [track]);

  // In-app "Chat with Support" CTAs (banners, error panes, etc.) live outside the appbar
  // and can't reach the widget directly, so they emit on supportService.openChatbot$ and
  // we expand the widget here. Guarded by isChatbotEnabled — the widget only exists then.
  useEffect(() => {
    if (!isChatbotEnabled) return undefined;
    const sub = supportService.openChatbot$.subscribe(() => {
      setMinimized(false);
      track(ChatbotTracking.TRACKER_CHATBOT_OPENED, { source: 'in_app_cta' });
    });
    return () => sub.unsubscribe();
  }, [isChatbotEnabled, supportService, track]);

  const handleChatOpen = () => {
    if (isChatbotEnabled) {
      setMinimized(false);
      track(ChatbotTracking.TRACKER_CHATBOT_OPENED);
    } else {
      // Fallback to Zendesk (chatbot disabled for this user): clear the boot-time
      // suppression flag so index.html's restore-open guard doesn't re-close it.
      window.__hevoSuppressZendeskChat = false;
      supportService.show();
    }
  };

  // Source/citation link clicks have no toolkit callback, so we delegate a click
  // listener on the container we own. The widget renders each source as
  // `<a class="_sourceLink_<hash>">`; CSS-modules keep the local name "sourceLink"
  // as a substring across rebuilds, so `a[class*="sourceLink"]` is a stable match.
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = containerRef.current;
    if (!isChatbotEnabled || !el) return undefined;

    const onClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      const anchor = target?.closest?.<HTMLAnchorElement>('a[class*="sourceLink"]');
      if (!anchor) return;
      let host = '';
      try {
        host = new URL(anchor.href).host;
      } catch {
        // ignore unparseable hrefs
      }
      track(ChatbotTracking.TRACKER_CHATBOT_SOURCE_CLICKED, host ? { host } : {});
    };

    el.addEventListener('click', onClick, true);
    return () => el.removeEventListener('click', onClick, true);
  }, [isChatbotEnabled, track]);

  // Two global rules, injected only while the chatbot is enabled:
  //  1. Hide the legacy Zendesk floating button (#zendesk_default_launcher — injected
  //     imperatively on DOMContentLoaded, which races React mount and flashes in on login).
  //  2. Lift the header's support (headset) menu above the widget's session/history shimmer
  //     overlays. In the toolkit both use z-index:10 in the same stacking context, so the
  //     overlay (later in the DOM) paints over the menu. The menu div carries role="menu",
  //     so the two-attribute selector out-specifies the toolkit's single-class rule.
  useEffect(() => {
    if (!isChatbotEnabled) return undefined;
    const style = document.createElement('style');
    style.setAttribute('data-cerebra-hide-zendesk-launcher', '');
    style.textContent =
      '#zendesk_default_launcher { display: none !important; }' +
      '[class*="supportMenu"][role="menu"] { z-index: 20; }';
    document.head.appendChild(style);
    return () => style.remove();
  }, [isChatbotEnabled]);

  // Draggable FAB: once the user drags it, `fabPos` holds the container's distance from the
  // viewport's right/bottom edges; null keeps the default bottom-right corner. Anchoring by
  // right/bottom (not left/top) pins the corner the FAB and the expanded panel share, so the
  // FAB comes back to exactly where it was dragged after the panel is opened and closed.
  const [fabPos, setFabPos] = useState<{ right: number; bottom: number } | null>(null);
  // The container's actually-applied right/bottom. While minimized this mirrors `fabPos`;
  // while expanded it is `fabPos` clamped against the (larger) panel so the whole window
  // stays on-screen — WITHOUT overwriting `fabPos`, so minimizing returns to the drag spot.
  const [renderPos, setRenderPos] = useState<{ right: number; bottom: number } | null>(null);
  // Set true on pointer-up after a real drag, so the click that follows is swallowed
  // (see onClickCapture below) instead of expanding the widget. Cleared at the start of
  // every new interaction so a later genuine tap is never eaten.
  const justDraggedRef = useRef(false);

  const handleFabPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    // Drag only from the FAB (minimized) and not with a secondary/middle button.
    const el = containerRef.current;
    if (!minimized || event.button > 0 || !el) return;
    justDraggedRef.current = false;

    const startX = event.clientX;
    const startY = event.clientY;
    const rect = el.getBoundingClientRect();
    const originRight = window.innerWidth - rect.right;
    const originBottom = window.innerHeight - rect.bottom;
    let moved = false;
    let last: { right: number; bottom: number } | null = null;

    const onMove = (e: PointerEvent) => {
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;
      if (!moved && Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;
      moved = true;
      const box = el.getBoundingClientRect();
      // Moving the pointer right/down shrinks the distance from the right/bottom edges.
      last = clampToViewport(originRight - dx, originBottom - dy, box.width, box.height);
      // Move imperatively during the drag so the widget doesn't re-render every frame;
      // the final position is committed to state on pointer-up.
      el.style.right = `${last.right}px`;
      el.style.bottom = `${last.bottom}px`;
    };

    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      if (moved && last) {
        setFabPos(last);
        justDraggedRef.current = true;
      }
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  // Derive the applied position by measuring the container's real size (FAB when minimized,
  // full panel when expanded) and clamping the stored FAB position against it, so the whole
  // window always fits in the viewport. Runs after every expand/collapse and on resize. Using
  // a layout effect means the clamp is applied before paint — no visible off-screen flash.
  useLayoutEffect(() => {
    const apply = () => {
      const el = containerRef.current;
      if (!el) return;
      if (!fabPos) {
        // Untouched: keep the default bottom-right corner.
        setRenderPos(null);
        return;
      }
      const box = el.getBoundingClientRect();
      setRenderPos(clampToViewport(fabPos.right, fabPos.bottom, box.width, box.height));
    };
    apply();
    window.addEventListener('resize', apply);
    return () => window.removeEventListener('resize', apply);
  }, [minimized, fabPos]);

  const pos = renderPos ?? { right: 16, bottom: 16 };
  const fabContainerStyle: CSSProperties = {
    position: 'fixed',
    zIndex: 9998,
    right: pos.right,
    bottom: pos.bottom,
    ...(minimized ? { cursor: 'grab', touchAction: 'none' } : {})
  };

  return (
    <>
      {isTeamInTrial || !EnvConfig.isCombinedSupportOptionsEnabled() ? (
        <AppbarSupportCTA
          label='Live Chat'
          badgeContent={zendeskUnreadCount ? getUnreadDisplayCount(zendeskUnreadCount) : undefined}
          showOnCallSupportIndicator={canShowOnCallSupportIndicator}
          supportPersonImageURL={supportPersonImageURL}
          trackingEvent={TRACKER_SUPPORT_CTA_CLICK}
          expanded={expanded}
          onClick={handleChatOpen}
        />
      ) : (
        <AppbarSupportCTAWithMenu
          supportUnreadCount={zendeskUnreadCount}
          canShowOnCallSupportIndicator={canShowOnCallSupportIndicator}
          expanded={expanded}
          onChatOpen={handleChatOpen}
        />
      )}

      {isChatbotEnabled && (
        <div
          ref={containerRef}
          style={fabContainerStyle}
          onPointerDown={handleFabPointerDown}
          onClickCapture={event => {
            // Suppress the click that trails a drag so it doesn't expand the widget.
            if (justDraggedRef.current) {
              justDraggedRef.current = false;
              event.stopPropagation();
              event.preventDefault();
            }
          }}
        >
          <ChatWidget
            title='Hevo Support'
            dataId='hevo-support-cerebra'
            ai={aiConfig}
            storage={storage}
            height={630}
            showMinimize
            showNewChat
            showHistory
            showSupport
            isMinimized={minimized}
            onMinimizedChange={setMinimized}
            showSessionTitle={false}
            pollInterval={10000}
            maxAttachmentSize={25 * 1024 * 1024} // 25 MB
            defaultView={ChatDefaultView.History}
            notificationSound={true}
            introMessage='👋 Hi there! Need help?'
            introMessageDelay={10000}
            showIdleNudge={true}
            idleNudgeTimeout={900000} // 15 minutes
            idleNudgeMessage='Still there? I can help you with your queries.'
            feedbackReasons={FEEDBACK_REASONS}
            priorityOptions={[
              { label: 'Business Critical', value: CustomerPriority.BusinessCritical },
              { label: 'Major', value: CustomerPriority.Major },
              { label: 'Minor', value: CustomerPriority.Minor },
              { label: 'No Impact', value: CustomerPriority.NoImpact }
            ]}
            onNewChat={() => {
              track(ChatbotTracking.TRACKER_CHATBOT_NEW_CHAT);
              storage.clearSession();
            }}
          />
        </div>
      )}
    </>
  );
}
